import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { SHOW_REVIEW_LINK_OPTIONS } from 'pages/appSettings/preferencesSettings/ReviewSettings/options.constants';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import api from '../services/api.service';

type TAppsPrefReviewSettingsState = {
  loading: boolean;
  hasErrors: boolean;
  viewLink: TTEPreferencesViewLink;
};

export type TTEPreferencesViewLink = {
  viewLinkSubmitter?: TViewLinkSubmitter;
  viewerEntry?: string;
  viewerPath?: string;
  viewerPage?: string;
  commentField?: string;
  currentPeriod?: string;
  reservationMode?: number;
};
export const VIEW_LINK_ENDPOINT = '/apps/TE_PREFERENCES/view-link';

export type TViewLinkSubmitter =
  (typeof SHOW_REVIEW_LINK_OPTIONS)[keyof typeof SHOW_REVIEW_LINK_OPTIONS]['value'];

export const initialState: TAppsPrefReviewSettingsState = {
  loading: false,
  hasErrors: false,
  viewLink: {},
};

// Slice prefReviewSettings
const slice = createSlice({
  name: 'appsPrefReviewSettings',
  initialState,
  reducers: {
    fetchData: (state) => {
      isLoadingRequest(state);
    },
    fetchDataSuccess: (
      state,
      { payload }: PayloadAction<TTEPreferencesViewLink>
    ) => {
      state.viewLink = payload;
      finishedLoadingSuccess(state);
    },
    fetchDataFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const PrefReviewLoading = (state: TRootState) =>
  state.appsPrefReviewSettings.loading;
export const PrefReviewSelector = (state: TRootState) =>
  state.appsPrefReviewSettings.viewLink;

// Actions
export const { fetchData, fetchDataSuccess, fetchDataFailure } = slice.actions;

export const getViewLink = createAppAsyncThunk(
  'appsPrefReviewSettings/getViewLink',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchData());
      const data = await api.get({
        endpoint: VIEW_LINK_ENDPOINT,
      });
      dispatch(fetchDataSuccess(data));
    } catch (e) {
      dispatch(fetchDataFailure());
      return console.error(e);
    }
  }
);

export const updatePrefReview = createAppAsyncThunk<
  void,
  TTEPreferencesViewLink
>('appsPrefReviewSettings/updatePrefReview', async (dataBody, { dispatch }) => {
  try {
    await api.patch({
      endpoint: VIEW_LINK_ENDPOINT,
      data: dataBody,
      successMessage: 'Settings saved',
    });
    dispatch(getViewLink());
  } catch (e) {
    dispatch(fetchDataFailure());
    return console.error(e);
  }
});
